import React, { useState, useRef, useEffect } from 'react';
import ReactCountryFlag from "react-country-flag";
import { TOffer, TPartner } from 'types/offers-temporary';
import './_partners-list.scss';
import OfferDragGhost from '../item/offer-drag-ghost';
import { DropResult } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faImage } from '@fortawesome/free-solid-svg-icons';
import { getOfferImageUrl, hasOfferImage } from 'utils/offer-image';
import { isMfoLikePanelOfferwallType } from 'types/offerwall-type';

interface PartnersSubListProps {
  partnerId: string;
  partner: TPartner;
  offers: TOffer[];
  findOfferSection: (offerId: number) => number | null;
  onDragStart: (offer: TOffer) => void;
  onDragEnd: (result: DropResult) => void;
  isExpanded?: boolean;
  expandRevision?: number;
  groupKey: string;
  onExpandedChange: (groupKey: string, expanded: boolean) => void;
  getOfferHiddenState: (offerId: number) => boolean;
}

const PartnersSubList: React.FC<PartnersSubListProps> = ({ 
  partnerId, 
  partner, 
  offers, 
  findOfferSection, 
  onDragStart, 
  onDragEnd,
  isExpanded: forceExpanded,
  expandRevision,
  groupKey,
  onExpandedChange,
  getOfferHiddenState
}) => {
  const [isLocalExpanded, setIsLocalExpanded] = useState(false);
  const [currentOffer, setCurrentOffer] = useState<TOffer | null>(null);
  const dragImageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceExpanded !== undefined) {
      setIsLocalExpanded(forceExpanded);
      onExpandedChange(groupKey, forceExpanded);
    }
  }, [forceExpanded, expandRevision, groupKey, onExpandedChange]);

  const handleToggleExpanded = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsLocalExpanded((expanded) => {
      const nextExpanded = !expanded;
      onExpandedChange(groupKey, nextExpanded);
      return nextExpanded;
    });
  };

  const handleDragStart = (e: React.DragEvent, offer: TOffer) => {
    const sectionId = findOfferSection(offer.id);
    if (sectionId !== null) {
      e.preventDefault();
      return;
    }

    setCurrentOffer(offer);
    onDragStart?.(offer);
    
    e.dataTransfer.setData('text/plain', JSON.stringify({
      ...offer,
      id: offer.id,
      name: offer.name,
      partnerId: partnerId,
      graphicFolder: offer.graphicFolder,
      geo: offer.geo
    }));

    if (dragImageRef.current) {
      dragImageRef.current.style.display = 'flex';
      dragImageRef.current.style.opacity = '1';
      dragImageRef.current.style.transition = 'none';
      e.dataTransfer.setDragImage(dragImageRef.current, 100, 25);
      
      setTimeout(() => {
        if (dragImageRef.current) {
          dragImageRef.current.style.display = 'none';
        }
      }, 0);
    }
  };

  const handleDragEnd = () => {
    if (dragImageRef.current) {
      dragImageRef.current.style.display = 'flex';
      dragImageRef.current.style.transition = 'opacity 0.2s ease-out';
      dragImageRef.current.style.opacity = '0';
      
      setTimeout(() => {
        if (dragImageRef.current) {
          dragImageRef.current.style.display = 'none';
          dragImageRef.current.style.opacity = '1';
          dragImageRef.current.style.transition = 'none';
        }
        setCurrentOffer(null);
      }, 200);
    }
    onDragEnd({
      destination: null,
      source: { index: 0, droppableId: '' },
      draggableId: '',
      reason: 'DROP',
      mode: 'FLUID',
      type: 'DEFAULT',
      combine: null
    });
  };

  // Group offers by geo (country)
  const groupedOffers = offers.reduce((acc, offer) => {
    const group = offer.geo || 'Other';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(offer);
    return acc;
  }, {} as Record<string, TOffer[]>);

  const getSectionTitle = (id: number) => {
    switch (id) {
      case 1:
        return '🎰 ПЕРЕВІР СВОЮ ВДАЧУ! 🎰';
      case 2:
        return '🥇 СТАВ І ВИГРАВАЙ! 🥇';
      default:
        return `Section ${id}`;
    }
  };

  if (offers.length === 0) return null;

  return (
    <div className="partners-section" data-expanded={isLocalExpanded}>
      <div className="partners-title" 
        onClick={handleToggleExpanded}
        data-expanded={isLocalExpanded}
      >
        <div className="partners-header">
          <span className="partners-name">{partner.name}</span>
        </div>
        <div className="header-indicators">
          {offers.map((offer) => {
            const sectionId = findOfferSection(offer.id);
            const isHidden = getOfferHiddenState(offer.id);
            return (
              <div 
                key={offer.id}
                className={`offer-indicator ${sectionId !== null ? 'offer-indicator--in-section' : ''} ${isHidden ? 'offer-indicator--hidden' : ''}`}
              />
            );
          })}
        </div>
        <span className="section-expand-icon">
          <FontAwesomeIcon icon={faCaretDown} />
        </span>
      </div>
      {isLocalExpanded && (
        <div className="partners-list-container">
          {Object.entries(groupedOffers).map(([countryCode, groupOffers]) => (
            <React.Fragment key={countryCode}>
              <div className="partners-subgroup-header">
                <ReactCountryFlag 
                  style={{
                    fontSize: '1rem',
                    lineHeight: '1rem',
                    marginRight: '8px'
                  }}
                  countryCode={countryCode} 
                />
                {countryCode}
              </div>
              {groupOffers.map((offer) => {
                const sectionId = findOfferSection(offer.id);
                const isHidden = getOfferHiddenState(offer.id);
                const showIndicator = sectionId !== null || isHidden;
                return (
                  <div
                    key={offer.id}
                    className={`partners-offer-item ${sectionId !== null ? 'partners-offer-item--disabled' : ''}`}
                    draggable={sectionId === null}
                    onDragStart={(e) => handleDragStart(e, offer)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="partners-offer-logo">
                      {hasOfferImage(offer) ? (
                        isMfoLikePanelOfferwallType((offer as any).offerwallType) ? (
                          <img className="partners-offer-thumbnail-bg" src={getOfferImageUrl(offer, 'bg')} />
                        ) : (
                          <>
                            <img className="partners-offer-thumbnail-bg" src={getOfferImageUrl(offer, 'bg')} />
                            <img className="partners-offer-thumbnail-fg" src={getOfferImageUrl(offer, 'logo')} />
                          </>
                        )
                      ) : (
                        <div className="partners-offer-thumbnail-placeholder">
                          <FontAwesomeIcon icon={faImage} />
                        </div>
                      )}
                    </div>
                    <div className="partners-offer-details">
                      <div className="partners-offer-name">{offer.caption}</div>
                      {sectionId !== null && (
                        <div className="partners-offer-section">{getSectionTitle(sectionId)}</div>
                      )}
                    </div>
                    {showIndicator && (
                      <div className={`offer-item-indicator ${sectionId !== null ? 'offer-item-indicator--in-section' : ''} ${isHidden ? 'offer-item-indicator--hidden' : ''}`} />
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}
      
      <OfferDragGhost 
        offer={currentOffer} 
        dragImageRef={dragImageRef}
        partnerName={partner.name}
        countryCode={currentOffer?.geo}
      />
    </div>
  );
};

export default PartnersSubList;
